document.documentElement.classList.remove("no-js");

const qs = (selector, scope = document) => scope.querySelector(selector);
const qsa = (selector, scope = document) => [
  ...scope.querySelectorAll(selector),
];

const bind = (element, key, callback) => {
  if (element.dataset[key]) return;
  element.dataset[key] = "true";
  callback(element);
};

const initDrawers = (scope) => {
  qsa("[data-drawer-open]", scope).forEach((button) =>
    bind(button, "drawerBound", () => {
      button.addEventListener("click", () => {
        const drawer = qs(`#${button.dataset.drawerOpen}`);
        if (!drawer) return;
        drawer.setAttribute("aria-hidden", "false");
        drawer.classList.add("is-open");
        document.body.classList.add("menu-open");
        qs("button, a, input", drawer)?.focus();
      });
    }),
  );
  qsa("[data-drawer-close]", scope).forEach((button) =>
    bind(button, "drawerBound", () => {
      button.addEventListener("click", () => {
        const drawer = button.closest("[aria-hidden]");
        drawer?.setAttribute("aria-hidden", "true");
        drawer?.classList.remove("is-open");
        document.body.classList.remove("menu-open");
      });
    }),
  );
};

const initQuantity = (scope) => {
  qsa("[data-quantity]", scope).forEach((quantity) =>
    bind(quantity, "quantityBound", () => {
      const input = qs("input", quantity);
      qsa("button", quantity).forEach((button) => {
        button.addEventListener("click", () => {
          const step = Number(button.dataset.step);
          input.value = Math.max(
            Number(input.min || 1),
            Number(input.value || 1) + step,
          );
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      });
    }),
  );
};

const initProducts = (scope) => {
  qsa("[data-product-root]", scope).forEach((root) =>
    bind(root, "productBound", () => {
      const variants = JSON.parse(
        qs("[data-variants]", root)?.textContent || "[]",
      );
      const optionGroups = qsa("[data-option-group]", root);
      const forms = qsa("[data-product-form]", root);
      const sticky = root.nextElementSibling?.matches("[data-sticky-atc]")
        ? root.nextElementSibling
        : null;
      const formatPrice = (price) => {
        if (window.Shopify?.formatMoney) {
          return window.Shopify.formatMoney(price);
        }
        try {
          return new Intl.NumberFormat(document.documentElement.lang || "en", {
            style: "currency",
            currency: root.dataset.currency || "PKR",
            maximumFractionDigits: 0,
          }).format(price / 100);
        } catch {
          return (price / 100).toFixed(2);
        }
      };
      const updateVariant = () => {
        const selections = optionGroups.map(
          (group) => qs("input:checked", group)?.value,
        );
        const variant = variants.find((item) =>
          item.options.every((option, index) => option === selections[index]),
        );
        if (!variant) return;
        optionGroups.forEach((group) => {
          const selected = qs("input:checked", group)?.value;
          const valueLabel = qs("legend span", group);
          if (valueLabel && selected) valueLabel.textContent = selected;
        });
        forms.forEach((form) => {
          const variantInput = qs('[name="id"]', form);
          if (variantInput) variantInput.value = variant.id;
          const button = qs('button[type="submit"]', form);
          if (!button) return;
          button.disabled = !variant.available;
          const label = qs("span", button);
          if (label) {
            label.textContent = variant.available
              ? button.dataset.available
              : button.dataset.sold;
          }
        });
        qsa("[data-price]", root).forEach((price) => {
          price.textContent = formatPrice(variant.price);
        });
        if (sticky) {
          const stickyButton = qs("[data-sticky-button]", sticky);
          const stickyPrice = qs("[data-sticky-price]", sticky);
          if (stickyPrice) stickyPrice.textContent = formatPrice(variant.price);
          if (stickyButton) {
            stickyButton.disabled = !variant.available;
            stickyButton.textContent = variant.available
              ? stickyButton.dataset.available
              : stickyButton.dataset.sold;
          }
        }
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        history.replaceState(history.state, "", url);
      };
      qsa('input[type="radio"]', root).forEach((input) =>
        input.addEventListener("change", updateVariant),
      );
    }),
  );
};

const initTabs = (scope) => {
  qsa("[data-tabs]", scope).forEach((tabs) =>
    bind(tabs, "tabsBound", () => {
      qsa('[role="tab"]', tabs).forEach((tab) => {
        tab.addEventListener("click", () => {
          qsa('[role="tab"]', tabs).forEach((item) =>
            item.setAttribute("aria-selected", String(item === tab)),
          );
          qsa('[role="tabpanel"]', tabs).forEach(
            (panel) =>
              (panel.hidden = panel.id !== tab.getAttribute("aria-controls")),
          );
        });
      });
    }),
  );
};

const initGalleries = (scope) => {
  qsa("[data-gallery]", scope).forEach((gallery) =>
    bind(gallery, "galleryBound", () => {
      const thumbs = qsa("[data-gallery-thumb]", gallery);
      const dots = qsa("[data-gallery-dot]", gallery);
      const arrows = qsa("[data-gallery-arrow]", gallery);
      const track = qs("[data-mobile-track]", gallery);
      const slides = qsa("[data-mobile-slide]", gallery);
      const main = qs(".pdp-main-image", gallery);
      const mainMedia = qs(".pdp-main-media--desktop", gallery);
      const zoomButton = qs("[data-gallery-zoom]", gallery);
      const status = qs("[data-gallery-status]", gallery);
      const mobileQuery = window.matchMedia("(max-width: 989px)");
      const reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      );
      let scrollFrame;

      const setActive = (index, centerThumb = false) => {
        if (index < 0 || index >= Math.max(thumbs.length, slides.length)) {
          return;
        }
        thumbs.forEach((thumb, thumbIndex) => {
          const active = thumbIndex === index;
          thumb.classList.toggle("is-active", active);
          thumb.setAttribute("aria-pressed", String(active));
        });
        dots.forEach((dot, dotIndex) => {
          const active = dotIndex === index;
          dot.classList.toggle("is-active", active);
          dot.setAttribute("aria-current", String(active));
        });
        if (status) {
          status.textContent = `Product image ${index + 1} of ${Math.max(
            thumbs.length,
            slides.length,
          )}`;
        }
        if (centerThumb && thumbs[index]) {
          thumbs[index].scrollIntoView({
            behavior: reducedMotion.matches ? "auto" : "smooth",
            block: "nearest",
            inline: "center",
          });
        }
      };

      const activeIndex = () => {
        const index = thumbs.findIndex((thumb) =>
          thumb.classList.contains("is-active"),
        );
        return index < 0 ? 0 : index;
      };

      const showImage = (index) => {
        const thumb = thumbs[index];
        if (!thumb) return;
        mainMedia?.classList.remove("is-zoomed");
        zoomButton?.setAttribute("aria-pressed", "false");
        zoomButton?.setAttribute("aria-label", "Zoom product image");
        if (mobileQuery.matches && track && slides[index]) {
          track.scrollTo({
            left: slides[index].offsetLeft,
            behavior: reducedMotion.matches ? "auto" : "smooth",
          });
        } else if (main) {
          main.src = thumb.dataset.src;
          if (thumb.dataset.srcset) main.srcset = thumb.dataset.srcset;
          main.alt = thumb.dataset.alt || "";
        }
        setActive(index, mobileQuery.matches);
      };

      thumbs.forEach((thumb, index) =>
        thumb.addEventListener("click", () => showImage(index)),
      );
      dots.forEach((dot, index) =>
        dot.addEventListener("click", () => showImage(index)),
      );
      arrows.forEach((arrow) =>
        arrow.addEventListener("click", () => {
          const count = Math.max(thumbs.length, slides.length);
          if (count < 2) return;
          const direction = arrow.dataset.galleryArrow === "next" ? 1 : -1;
          showImage((activeIndex() + direction + count) % count);
        }),
      );
      zoomButton?.addEventListener("click", () => {
        if (!mainMedia) return;
        const zoomed = mainMedia.classList.toggle("is-zoomed");
        zoomButton.setAttribute("aria-pressed", String(zoomed));
        zoomButton.setAttribute(
          "aria-label",
          zoomed ? "Reset product image zoom" : "Zoom product image",
        );
      });

      if (track && slides.length) {
        track.addEventListener(
          "scroll",
          () => {
            window.cancelAnimationFrame(scrollFrame);
            scrollFrame = window.requestAnimationFrame(() => {
              const index = slides.reduce(
                (nearest, slide, slideIndex) =>
                  Math.abs(slide.offsetLeft - track.scrollLeft) <
                  Math.abs(slides[nearest].offsetLeft - track.scrollLeft)
                    ? slideIndex
                    : nearest,
                0,
              );
              setActive(index, true);
            });
          },
          { passive: true },
        );
        track.addEventListener("keydown", (event) => {
          if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
          event.preventDefault();
          const direction = event.key === "ArrowRight" ? 1 : -1;
          const index = Math.min(
            slides.length - 1,
            Math.max(0, activeIndex() + direction),
          );
          showImage(index);
        });
      }

      const initialIndex = activeIndex();
      setActive(initialIndex);
      if (track && slides[initialIndex]) {
        window.requestAnimationFrame(() => {
          track.scrollLeft = slides[initialIndex].offsetLeft;
        });
      }
    }),
  );
};

const initRails = (scope) => {
  qsa("[data-rail]", scope).forEach((rail) =>
    bind(rail, "railBound", () => {
      qsa("[data-rail-direction]", rail).forEach((button) => {
        button.addEventListener("click", () => {
          qs("[data-rail-track]", rail)?.scrollBy({
            left: button.dataset.railDirection === "next" ? 360 : -360,
            behavior: "smooth",
          });
        });
      });
    }),
  );
};

const initStickyCart = (scope) => {
  qsa("[data-sticky-atc]", scope).forEach((sticky) =>
    bind(sticky, "stickyBound", () => {
      const root = sticky.previousElementSibling;
      const primary = qs("[data-primary-atc]", root || document);
      if (!primary) return;
      const updateSticky = () => sticky.classList.toggle("is-visible", primary.getBoundingClientRect().bottom <= 0);
      let stickyFrame;
      const scheduleSticky = () => {
        if (stickyFrame) return;
        stickyFrame = requestAnimationFrame(() => { stickyFrame = undefined; updateSticky(); });
      };
      new IntersectionObserver(scheduleSticky, { threshold: 0 }).observe(primary);
      window.addEventListener("scroll", scheduleSticky, { passive: true });
      window.addEventListener("resize", scheduleSticky, { passive: true });
      updateSticky();
      qs("[data-sticky-button]", sticky)?.addEventListener("click", () => {
        const submit = qs('button[type="submit"]', primary);
        if (!submit || submit.disabled) return;
        submit.click();
      });
    }),
  );
};

const initReveals = (scope) => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.12 },
  );
  qsa(".reveal", scope).forEach((element) =>
    bind(element, "revealBound", () => observer.observe(element)),
  );
};

const initTheme = (scope = document) => {
  initDrawers(scope);
  initQuantity(scope);
  initProducts(scope);
  initTabs(scope);
  initGalleries(scope);
  initRails(scope);
  initStickyCart(scope);
  initReveals(scope);
};

initTheme();
document.addEventListener("shopify:section:load", (event) =>
  initTheme(event.target),
);
document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") return;
  qsa('[aria-hidden="false"]').forEach((drawer) =>
    drawer.setAttribute("aria-hidden", "true"),
  );
  qsa(".is-open").forEach((drawer) => drawer.classList.remove("is-open"));
  document.body.classList.remove("menu-open", "dialog-open");
});
